/**
 * Утилиты для форматирования даты и времени в русском формате
 */

// Русские названия месяцев
const russianMonths = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

// Русские названия месяцев (краткие)
const russianMonthsShort = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
]

// Русские названия дней недели
const russianDays = [
  'воскресенье',
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
]

// Русские названия дней недели (краткие)
const russianDaysShort = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']

/**
 * Форматирует дату в русском формате
 * @param date - дата для форматирования
 * @param options - опции форматирования
 */
export function formatRussianDate(
  date: string | Date,
  options: {
    includeTime?: boolean
    includeYear?: boolean
    shortMonth?: boolean
    shortDay?: boolean
  } = {},
): string {
  const d = new Date(date)

  if (isNaN(d.getTime())) {
    return 'Неверная дата'
  }

  const {
    includeTime = false,
    includeYear = true,
    shortMonth = false,
    shortDay = false,
  } = options

  const day = d.getDate()
  const month = shortMonth
    ? russianMonthsShort[d.getMonth()]
    : russianMonths[d.getMonth()]
  const year = includeYear ? d.getFullYear() : ''
  const dayOfWeek = shortDay
    ? russianDaysShort[d.getDay()]
    : russianDays[d.getDay()]

  let result = `${day} ${month}`

  if (includeYear) {
    result += ` ${year}`
  }

  if (includeTime) {
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    result += `, ${hours}:${minutes}`
  }

  return result
}

/**
 * Форматирует время в 24-часовом формате
 * @param date - дата для форматирования
 */
export function formatRussianTime(date: string | Date): string {
  const d = new Date(date)

  if (isNaN(d.getTime())) {
    return '--:--'
  }

  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')

  return `${hours}:${minutes}`
}

/**
 * Форматирует дату для отображения в списке чатов
 * @param date - дата для форматирования
 */
export function formatChatListDate(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()

  if (isNaN(d.getTime())) {
    return ''
  }

  // Сегодня
  if (d.toDateString() === now.toDateString()) {
    return formatRussianTime(d)
  }

  // Вчера
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Вчера'
  }

  // Этот год
  if (d.getFullYear() === now.getFullYear()) {
    return formatRussianDate(d, { includeYear: false, shortMonth: true })
  }

  // Другой год
  return formatRussianDate(d, { includeYear: true, shortMonth: true })
}

/**
 * Форматирует дату для отображения в сообщениях
 * @param date - дата для форматирования
 */
export function formatMessageDate(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()

  if (isNaN(d.getTime())) {
    return ''
  }

  // Сегодня
  if (d.toDateString() === now.toDateString()) {
    return formatRussianTime(d)
  }

  // Вчера
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) {
    return `Вчера, ${formatRussianTime(d)}`
  }

  // Этот год
  if (d.getFullYear() === now.getFullYear()) {
    return `${formatRussianDate(d, {
      includeYear: false,
    })}, ${formatRussianTime(d)}`
  }

  // Другой год
  return `${formatRussianDate(d, { includeYear: true })}, ${formatRussianTime(
    d,
  )}`
}

/**
 * Форматирует дату для группировки сообщений
 * @param date - дата для форматирования
 */
export function formatMessageGroupDate(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()

  if (isNaN(d.getTime())) {
    return 'Неизвестная дата'
  }

  // Сегодня
  if (d.toDateString() === now.toDateString()) {
    return 'Сегодня'
  }

  // Вчера
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Вчера'
  }

  // Этот год
  if (d.getFullYear() === now.getFullYear()) {
    return formatRussianDate(d, { includeYear: false })
  }

  // Другой год
  return formatRussianDate(d, { includeYear: true })
}

function capitalizeFirst(value: string): string {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** Дата для галереи файлов и медиа: «Сегодня», «Пятница, 5 Июня» */
export function formatFilesMediaGroupDate(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()

  if (isNaN(d.getTime())) {
    return 'Неизвестная дата'
  }

  if (d.toDateString() === now.toDateString()) {
    return 'Сегодня'
  }

  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Вчера'
  }

  const dayOfWeek = capitalizeFirst(russianDays[d.getDay()])
  const day = d.getDate()
  const month = capitalizeFirst(russianMonths[d.getMonth()])

  if (d.getFullYear() === now.getFullYear()) {
    return `${dayOfWeek}, ${day} ${month}`
  }

  return `${dayOfWeek}, ${day} ${month} ${d.getFullYear()}`
}
